import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

/**
 * Hindi is the ONLY source language.  No HTML/DOM writes, no duplicate JSON,
 * no heavy browser ML bundle, and no changes to SEO metadata or image paths.
 *
 * Translation engine:
 *   1. Chrome's on-device Translator API (desktop, where supported), OR
 *   2. MyMemory public translation API (free, limited quota; other browsers).
 * Translation begins after an explicit click on English.
 */
const Context = createContext(null);
const HINDI = /[\u0900-\u097F]/;
const ATTRIBUTE_KEYS = ["alt", "placeholder", "aria-label", "title", "label"];
const CACHE_KEY = "vjm-katha-translation-v3";
const CHROME_CONFIG = { sourceLanguage: "hi", targetLanguage: "en" };

// Carefully translated *common* words and book titles.  This is NOT a second
// content library: all stories still come exclusively from original Hindi JSON.
const CURATED = {
  होम: "Home",
  "कथा संग्रह": "Katha Library",
  "✦ श्रीकृष्ण कथा": "✦ Stories of Lord Krishna",
  "श्रीकृष्ण कथा": "Stories of Lord Krishna",
  "श्रीकृष्ण बाल लीलाएँ": "Childhood Stories of Lord Krishna",
  "गोकुल की मधुर कथाएँ": "Beloved Stories of Gokul",
  "रामायण की कथाएँ": "Stories from the Ramayana",
  "शिव महिमा की कथाएँ": "Stories of Lord Shiva",
  "हनुमान जी की कथाएँ": "Stories of Lord Hanuman",
  "भक्तों की प्रेरक कथाएँ": "Inspiring Stories of Devotees",
  "भगवद्गीता से जीवन ज्ञान": "Life Lessons from the Bhagavad Gita",
  पुस्तकें: "Books",
  कथाएँ: "Stories",
  अध्याय: "Chapter",
  "पुस्तक पढ़ें": "Read Book",
  "पूरी कथा पढ़ें": "Read Full Story",
  "पिछली कथा": "Previous Story",
  "अगली कथा": "Next Story",
  "कथा का सार": "Lesson from this Story",
  "सभी पुस्तकें": "All Books",
  "सभी अध्याय देखें": "View All Chapters",
  "पूरी पुस्तक": "Full Book",
  "मूल भाषा": "Original Language",
  "केवल हिंदी": "Hindi",
  हिंदी: "Hindi",
  "हिंदी कथा": "Hindi Story",
  "हिंदी धार्मिक कथा": "Hindi Devotional Story",
  "पुस्तक संग्रह": "Book Collection",
  "लिंक कॉपी हो गया ✓": "Link copied ✓",
  "कथा का लिंक कॉपी करें": "Copy Story Link",
  "पुस्तक खोजें": "Search Books",
  खोजें: "Search",
  "सभी पुस्तकें देखें": "View All Books",
  "कथा खुल रही है…": "Loading story…",
  "पढ़ने के लिए एक कथा चुनें": "Choose a Story to Read",
  "इसी पुस्तक की अन्य कथाएँ": "More Stories from This Book",
};

function isHindi(value) {
  return typeof value === "string" && HINDI.test(value);
}

function getSaved() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CACHE_KEY) || "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
}

function saveCache(cache) {
  try {
    // Bound storage so even long stories won't fill the visitor's localStorage.
    const entries = Object.entries(cache).slice(-450);
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify(Object.fromEntries(entries)),
    );
  } catch {
    // Storage is optional (incognito, quotas, and privacy settings are fine).
  }
}

function gather(node, found) {
  if (Array.isArray(node)) {
    node.forEach((item) => gather(item, found));
    return;
  }
  if (isHindi(node)) found.add(node.trim());
  if (!React.isValidElement(node)) return;
  if (typeof node.type === "string") {
    ATTRIBUTE_KEYS.forEach((name) => {
      if (isHindi(node.props[name])) found.add(node.props[name].trim());
    });
  }
  gather(node.props.children, found);
}

function outputText(value, dictionary) {
  if (!isHindi(value)) return value;
  const cleaned = value.trim();
  const translated = CURATED[cleaned] || dictionary[cleaned];
  return translated ? value.replace(cleaned, translated) : value;
}

function renderTranslated(node, dictionary) {
  if (typeof node === "string") return outputText(node, dictionary);
  if (!React.isValidElement(node)) return node;
  const changes = {};
  if (typeof node.type === "string") {
    ATTRIBUTE_KEYS.forEach((name) => {
      if (isHindi(node.props[name]))
        changes[name] = outputText(node.props[name], dictionary);
    });
  }
  if (node.props.children !== undefined) {
    // React.Children.map normalizes and composes keys of child arrays/fragments.
    // Using plain Array.map + cloneElement caused the user's missing-key warnings.
    changes.children = React.Children.map(node.props.children, (child) =>
      renderTranslated(child, dictionary),
    );
  }
  // key/ref, href/src, route props, event handlers and component identity retained.
  return React.cloneElement(node, changes);
}

function splitByUtf8(text, maxBytes = 425) {
  const enc = new TextEncoder();
  const remaining = Array.from(text.trim());
  const chunks = [];
  while (remaining.length) {
    let i = 0;
    let bytes = 0;
    let breakAt = 0;
    while (i < remaining.length) {
      const byteCount = enc.encode(remaining[i]).length;
      if (bytes + byteCount > maxBytes) break;
      bytes += byteCount;
      i += 1;
      if (/[।.!?\s]/.test(remaining[i - 1])) breakAt = i;
    }
    if (!i) throw new Error("Could not split translation input safely.");
    const cut =
      i === remaining.length ? i : breakAt > Math.floor(i / 2) ? breakAt : i;
    chunks.push(remaining.splice(0, cut).join("").trim());
  }
  return chunks.filter(Boolean);
}

async function translateOnline(original) {
  const segments = splitByUtf8(original);
  const translatePart = async (segment) => {
    const params = new URLSearchParams({ q: segment, langpair: "hi|en" });
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 14000);
    let response;
    try {
      response = await fetch(
        `https://api.mymemory.translated.net/get?${params}`,
        {
          signal: controller.signal,
          headers: { Accept: "application/json" },
        },
      );
    } finally {
      clearTimeout(timeout);
    }
    if (!response.ok)
      throw new Error(`Online translator returned ${response.status}.`);
    const data = await response.json();
    const translated = data?.responseData?.translatedText;
    if (
      data?.quotaFinished ||
      Number(data?.responseStatus || 200) !== 200 ||
      !translated ||
      /MYMEMORY WARNING|QUERY LENGTH LIMIT/i.test(translated)
    ) {
      throw new Error("Free translation limit reached or service unavailable.");
    }
    return translated
      .replace(/&quot;/gi, '"')
      .replace(/&#39;|&#x27;/gi, "'")
      .replace(/&amp;/gi, "&");
  };
  const results = [];
  // At most two public-API requests concurrently. Preserves text order.
  for (let i = 0; i < segments.length; i += 2) {
    const translated = await Promise.all(
      segments.slice(i, i + 2).map(translatePart),
    );
    results.push(...translated);
  }
  return results.join(" ");
}

function getNativeTranslator(onProgress) {
  // Must be called from the user clicking 'English': Chrome requires a gesture
  // to download the translation language pack when it isn't installed yet.
  if (typeof window.Translator?.create !== "function") return null;
  return window.Translator.create({
    ...CHROME_CONFIG,
    monitor(monitor) {
      monitor.addEventListener("downloadprogress", (e) => {
        onProgress(Math.min(100, Math.round((e.loaded || 0) * 100)));
      });
    },
  });
}

export function KathaTranslationProvider({ children }) {
  const [language, setLanguage] = useState("hi");
  const [dictionary, setDictionary] = useState(getSaved);
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState({ complete: 0, total: 0 });
  const [error, setError] = useState("");
  const [download, setDownload] = useState(0);
  const languageRef = useRef("hi");
  const queue = useRef([]);
  const pending = useRef(new Set());
  const cache = useRef(dictionary);
  const processing = useRef(false);
  const activeScope = useRef("");
  const nativePromise = useRef(null);
  const backend = useRef("online");

  const chooseLanguage = useCallback((next) => {
    const selected = next === "en" ? "en" : "hi";
    languageRef.current = selected;
    setLanguage(selected);
    setError("");
    if (selected === "hi") {
      queue.current = [];
      pending.current.clear();
      setStatus("idle");
      return;
    }
    setStatus("loading");
    // Do not add an await before this call: it must be initiated directly
    // by the reader's click to enable Chrome model download.
    if (!nativePromise.current) {
      try {
        const started = getNativeTranslator(setDownload);
        nativePromise.current = started
          ? Promise.resolve(started)
              .then((translator) => {
                backend.current = "native";
                return translator;
              })
              .catch(() => {
                backend.current = "online";
                return null;
              })
          : Promise.resolve(null);
      } catch {
        backend.current = "online";
        nativePromise.current = Promise.resolve(null);
      }
    }
  }, []);

  const runQueue = useCallback(async () => {
    if (processing.current) return;
    processing.current = true;
    try {
      const translator = await (nativePromise.current || Promise.resolve(null));
      if (languageRef.current !== "en") return;
      setStatus("translating");
      while (queue.current.length && languageRef.current === "en") {
        const { text, scope } = queue.current.shift();
        if (scope !== activeScope.current) {
          pending.current.delete(text);
          continue;
        }
        try {
          const translated = translator
            ? await translator.translate(text)
            : await translateOnline(text);
          if (typeof translated !== "string" || !translated.trim()) {
            throw new Error("Translator returned empty text.");
          }
          cache.current[text] = translated.trim();
          setDictionary((prev) => ({ ...prev, [text]: translated.trim() }));
          if (scope === activeScope.current) {
            setProgress((prev) => ({ ...prev, complete: prev.complete + 1 }));
          }
          if (Object.keys(cache.current).length % 8 === 0)
            saveCache(cache.current);
        } catch (e) {
          // A service or quota failure must never display a made-up translation.
          setError(e?.message || "Translation unavailable.");
          setStatus("error");
          queue.current = [];
          pending.current.clear();
          break;
        } finally {
          pending.current.delete(text);
        }
      }
      if (languageRef.current === "en" && queue.current.length === 0) {
        setStatus((value) => (value === "error" ? value : "ready"));
        saveCache(cache.current);
      }
    } finally {
      processing.current = false;
      if (queue.current.length && languageRef.current === "en") {
        Promise.resolve().then(runQueue);
      }
    }
  }, []);

  const requestTexts = useCallback(
    (texts, scope) => {
      if (languageRef.current !== "en") return;
      if (scope !== activeScope.current) {
        activeScope.current = scope;
        queue.current = [];
        pending.current.clear();
        setProgress({ complete: 0, total: 0 });
      }
      const unseen = texts
        .filter(
          (text) =>
            isHindi(text) &&
            !CURATED[text] &&
            !cache.current[text] &&
            !pending.current.has(text),
        )
        // Translate smaller labels/headings first, then larger story paragraphs.
        .sort((a, b) => a.length - b.length);
      if (!unseen.length) return;
      unseen.forEach((text) => {
        pending.current.add(text);
        queue.current.push({ text, scope });
      });
      setProgress((prev) => ({ ...prev, total: prev.total + unseen.length }));
      runQueue();
    },
    [runQueue],
  );

  useEffect(
    () => () => {
      saveCache(cache.current);
      nativePromise.current
        ?.then((translator) => translator?.destroy?.())
        .catch(() => {});
    },
    [],
  );

  const context = useMemo(
    () => ({
      language,
      dictionary,
      chooseLanguage,
      requestTexts,
      status,
      progress,
      error,
      download,
      backend: backend.current,
    }),
    [
      language,
      dictionary,
      chooseLanguage,
      requestTexts,
      status,
      progress,
      error,
      download,
    ],
  );
  return <Context.Provider value={context}>{children}</Context.Provider>;
}

export function KathaLanguageBar() {
  const context = useContext(Context);
  if (!context) return null;
  const { language, chooseLanguage, status, progress, error, download } =
    context;
  return (
    <div
      className="katha-translation-toolbar"
      role="group"
      aria-label="Choose Katha reading language"
    >
      <span className="katha-translation-title">पढ़ने की भाषा / Read in:</span>
      <button
        type="button"
        className={language === "hi" ? "active" : ""}
        onClick={() => chooseLanguage("hi")}
        aria-pressed={language === "hi"}
      >
        हिंदी
      </button>
      <button
        type="button"
        className={language === "en" ? "active" : ""}
        onClick={() => chooseLanguage("en")}
        aria-pressed={language === "en"}
      >
        English
      </button>
      {language === "en" && status === "loading" && (
        <small role="status">
          Preparing translator{download > 0 ? ` · ${download}%` : "…"}
        </small>
      )}
      {language === "en" && status === "translating" && (
        <small role="status">
          Translating: {progress.complete}/{progress.total}
        </small>
      )}
      {language === "en" && error && (
        <small role="alert">
          Translation is limited right now. Untranslated parts remain in Hindi.{" "}
          {error}
        </small>
      )}
    </div>
  );
}

export function AutoTranslated({ children }) {
  const context = useContext(Context);
  const route = useLocation().pathname;
  const language = context?.language || "hi";
  const dictionary = context?.dictionary || {};
  const requestTexts = context?.requestTexts;
  const texts = useMemo(() => {
    if (language !== "en") return [];
    const found = new Set();
    gather(children, found);
    return [...found];
  }, [children, language]);
  const fingerprint = texts.join("\u001f");
  useEffect(() => {
    if (language === "en") requestTexts?.(texts, route);
  }, [language, route, requestTexts, fingerprint]);
  return (
    <>
      {language === "en"
        ? React.Children.map(children, (child) =>
            renderTranslated(child, dictionary),
          )
        : children}
    </>
  );
}
